import { formatAmount } from "../../../../utils/hooks/dataHandler";
import {
  AnnotationFlags,
  PDFAcroSignature,
  PDFDocument,
  PDFHexString,
  PDFName,
  PDFWidgetAnnotation,
  StandardFonts,
  rgb,
} from "pdf-lib";
import graTemplate from "../../../../forms/Form GR-A.pdf";
import grbTemplate from "../../../../forms/Form GR-B.pdf";
import { dateOnly } from "../domain/preparation";

// Rectangles verified against the supplied templates' widgets. The source
// widgets contain duplicate names, so generation rebuilds uniquely named,
// editable fields at these positions after copying each form into the output.
const GRA_ROWS = [
  {
    page: 0,
    order: [73, 338, 107, 356],
    name: [151, 335, 301, 357],
    taxRef: [182, 280, 308, 299],
    x: [460, 568],
    ys: [338, 308, 279, 252, 224, 197],
    from: [598, 307, 679, 324],
    to: [598, 254, 679, 271],
  },
  {
    page: 1,
    order: [36, 783, 69, 799],
    name: [113, 779, 263, 801],
    taxRef: [140, 725, 269, 742],
    x: [417, 522],
    ys: [783, 753, 725, 698, 671, 643],
    from: [553, 752, 632, 767],
    to: [553, 700, 632, 716],
  },
  {
    page: 1,
    order: [37, 604, 70, 619],
    name: [113, 600, 263, 622],
    taxRef: [141, 546, 270, 561],
    x: [417, 522],
    ys: [604, 573, 546, 519, 491, 464],
    from: [553, 573, 632, 588],
    to: [553, 521, 632, 536],
  },
  {
    page: 1,
    order: [37, 424, 70, 439],
    name: [113, 421, 263, 443],
    taxRef: [140, 367, 269, 382],
    x: [417, 522],
    ys: [425, 394, 367, 339, 313, 285],
    from: [553, 394, 632, 409],
    to: [553, 342, 632, 357],
  },
  {
    page: 1,
    order: [37, 245, 70, 261],
    name: [114, 241, 264, 263],
    taxRef: [141, 188, 270, 203],
    x: [417, 522],
    ys: [245, 215, 188, 160, 133, 105],
    from: [555, 215, 633, 230],
    to: [555, 163, 633, 177],
  },
];
const GRB_Y = [367, 323, 280, 237, 194, 151, 108];
const fields = ["caTransferred", "lossesTransferred", "donationsTransferred"];
const printedDate = (value) => {
  const normalized = dateOnly(value);
  if (!normalized)
    throw new Error("A valid date is required for form generation.");
  return normalized.split("-").reverse().join("/");
};

export function buildFormDocuments(result, type) {
  if (!["gra", "grb"].includes(type))
    throw new Error("Unknown Group Relief form.");
  if (!result?.transfers?.length || !result.reconciliation?.balanced)
    throw new Error(
      "Run an optimisation with reconciled transfers before generating forms.",
    );
  const end = dateOnly(result.settings?.commonFinancialYearEnd);
  if (!end || !Number.isInteger(Number(result.settings.yearOfAssessment)))
    throw new Error("The result has invalid case settings.");
  const companies = new Map(
    result.companies.map((company) => [company.id, company]),
  );
  const seen = new Set();
  const groups = new Map();
  const flow = Object.fromEntries(
    result.companies.map((c) => [c.id, [0, 0, 0]]),
  );
  result.transfers.forEach((transfer) => {
    const transferor = companies.get(transfer.transferorId);
    const claimant = companies.get(transfer.claimantId);
    const key = JSON.stringify([transfer.transferorId, transfer.claimantId]);
    if (
      !transferor ||
      !claimant ||
      transferor.id === claimant.id ||
      seen.has(key)
    )
      throw new Error("The transfer relationships are invalid.");
    seen.add(key);
    const amounts = fields.map((field) => transfer[field]);
    if (
      amounts.some((amount) => !Number.isSafeInteger(amount) || amount < 0) ||
      amounts.reduce((s, v) => s + v, 0) !== transfer.totalTransferred ||
      transfer.totalTransferred <= 0
    )
      throw new Error("The transfer amounts do not reconcile.");
    fields.forEach((field, index) => {
      flow[transferor.id][index] += transfer[field];
      flow[claimant.id][index] -= transfer[field];
    });
    const days = transfer.pairQualifyingDays;
    const limit = Math.min(
      transferor.continuousQualifyingDays,
      claimant.continuousQualifyingDays,
    );
    if (!Number.isInteger(days) || days < 1 || days !== limit)
      throw new Error(
        "Confirm the continuous shareholding period for every transfer.",
      );
    const start = new Date(`${end}T00:00:00Z`);
    start.setUTCDate(start.getUTCDate() - days + 1);
    const owner = type === "gra" ? transferor : claimant;
    const counterparty = type === "gra" ? claimant : transferor;
    if (
      !owner.name?.trim() ||
      !owner.uen?.trim() ||
      !counterparty.name?.trim() ||
      !counterparty.uen?.trim()
    )
      throw new Error("Company names and tax references are required.");
    if (!groups.has(owner.id)) groups.set(owner.id, { owner, rows: [] });
    const group = groups.get(owner.id);
    group.rows.push({
      priority: group.rows.length + 1,
      counterparty,
      from: start.toISOString().slice(0, 10),
      to: end,
      // The prototype and this optimiser support normal-rate items only.
      amounts: [
        transfer.caTransferred,
        0,
        transfer.lossesTransferred,
        0,
        transfer.donationsTransferred,
        0,
      ],
      total: transfer.totalTransferred,
    });
  });
  Object.entries(flow).forEach(([id, totals]) => {
    ["caTransfer", "lossesTransfer", "donationsTransfer"].forEach(
      (key, index) => {
        if (totals[index] !== result.adjustments?.[id]?.[key])
          throw new Error(
            "The company adjustments do not match the transfer schedule.",
          );
      },
    );
  });
  const size = type === "gra" ? 5 : 7;
  const documents = [];
  groups.forEach(({ owner, rows }) => {
    for (let offset = 0; offset < rows.length; offset += size) {
      const chunk = rows.slice(offset, offset + size);
      documents.push({
        type,
        owner,
        yearOfAssessment: Number(result.settings.yearOfAssessment),
        rows: chunk,
        total: chunk.reduce((s, r) => s + r.total, 0),
        batchNumber: offset / size + 1,
        batchCount: Math.ceil(rows.length / size),
      });
    }
  });
  return documents;
}

function fittedFontSize(
  font,
  value,
  rect,
  preferredSize = 9,
  multiline = false,
) {
  const text = String(value ?? "").trim();
  if (!text) return preferredSize;
  const [x1, y1, x2, y2] = rect;
  const width = x2 - x1 - 4,
    height = y2 - y1 - 3;
  let size = preferredSize,
    lines;
  if (!multiline) {
    while (size >= 4 && font.widthOfTextAtSize(text, size) > width) size -= 0.5;
    if (size < 4) throw new Error(`Text is too long for the form: ${text}`);
    return size;
  }
  // Fit every character, including long names/UENs; never silently truncate.
  while (size >= 4) {
    lines = [""];
    for (const word of text.split(/\s+/)) {
      const last = lines.length - 1;
      const candidate = lines[last] ? `${lines[last]} ${word}` : word;
      if (font.widthOfTextAtSize(candidate, size) <= width)
        lines[last] = candidate;
      else if (font.widthOfTextAtSize(word, size) <= width) lines.push(word);
      else {
        if (lines[last]) lines.push("");
        for (const char of word) {
          const index = lines.length - 1;
          if (font.widthOfTextAtSize(lines[index] + char, size) > width)
            lines.push(char);
          else lines[index] += char;
        }
      }
    }
    if (lines.length * size * 1.15 <= height) break;
    size -= 0.5;
  }
  if (size < 4) throw new Error(`Text is too long for the form: ${text}`);
  return size;
}

function addTextField(
  form,
  page,
  font,
  name,
  value,
  rect,
  preferredSize = 9,
  multiline = false,
) {
  const field = form.createTextField(name);
  const text = String(value ?? "").trim();
  if (multiline) field.enableMultiline();
  field.setText(text);
  const [x1, y1, x2, y2] = rect;
  field.addToPage(page, {
    x: x1,
    y: y1,
    width: x2 - x1,
    height: y2 - y1,
    font,
    textColor: rgb(0.05, 0.12, 0.1),
    backgroundColor: undefined,
    borderColor: undefined,
    borderWidth: 0,
  });
  field.setFontSize(fittedFontSize(font, text, rect, preferredSize, multiline));
  field.updateAppearances(font);
}

function addCheckBox(form, page, name, rect, checked) {
  const field = form.createCheckBox(name);
  const [x1, y1, x2, y2] = rect;
  field.addToPage(page, {
    x: x1,
    y: y1,
    width: x2 - x1,
    height: y2 - y1,
    backgroundColor: undefined,
    borderColor: undefined,
    borderWidth: 0,
  });
  if (checked) field.check();
}

function addSignatureField(document, page, name, rect) {
  const context = document.context;
  const dict = context.obj({
    FT: "Sig",
    Kids: [],
    T: PDFHexString.fromText(name),
    Ff: 0,
  });
  const fieldRef = context.register(dict);
  const signature = PDFAcroSignature.fromDict(dict, fieldRef);
  document.catalog.getOrCreateAcroForm().addField(fieldRef);
  const widget = PDFWidgetAnnotation.create(context, fieldRef);
  const [x1, y1, x2, y2] = rect;
  widget.setRectangle({ x: x1, y: y1, width: x2 - x1, height: y2 - y1 });
  widget.setP(page.ref);
  widget.setFlagTo(AnnotationFlags.Print, true);
  const widgetRef = context.register(widget.dict);
  signature.addWidget(widgetRef);
  page.node.addAnnot(widgetRef);
}

function addDeclarationFields(
  output,
  form,
  pages,
  font,
  type,
  prefix,
  detail = {},
) {
  const page = pages[0];
  const boxes =
    type === "gra"
      ? [
          [79, 446, 311, 468],
          [321, 442, 497, 468],
          [509, 442, 587, 468],
          [597, 444, 681, 468],
        ]
      : [
          [29, 445, 243, 467],
          [254, 443, 411, 465],
          [420, 444, 488, 465],
          [496, 443, 563, 466],
        ];
  const keys = ["fullName", "capacity", "contactNumber", "date"];
  [
    detail.fullName,
    detail.capacity,
    detail.contactNumber,
    detail.date ? printedDate(detail.date) : "",
  ].forEach((value, index) =>
    addTextField(
      form,
      page,
      font,
      `${prefix}_${keys[index]}`,
      value,
      boxes[index],
      8,
    ),
  );
  if (type === "gra") {
    [
      [80, 583, 98, 601],
      [80, 561, 98, 579],
      [80, 527, 98, 545],
    ].forEach((box, index) =>
      addCheckBox(
        form,
        page,
        `${prefix}_declaration_${index + 1}`,
        box,
        detail.transferorDeclarationsConfirmed === true,
      ),
    );
  }
  // Preserve a real, empty digital-signature field for the authorised person.
  addSignatureField(
    output,
    type === "gra" ? pages[1] : pages[0],
    `${prefix}_signature`,
    type === "gra" ? [460, 43, 610, 75] : [430.5, 1.9, 580.5, 33.9],
  );
}

export async function buildGroupReliefPdf(
  type,
  result,
  { templateBytes, declarations = {} } = {},
) {
  const documents = buildFormDocuments(result, type);
  if (!templateBytes) {
    const response = await fetch(type === "gra" ? graTemplate : grbTemplate);
    if (!response.ok)
      throw new Error("Unable to load the GR form template. Please retry.");
    templateBytes = await response.arrayBuffer();
  }
  const output = await PDFDocument.create();
  const outputForm = output.getForm();
  for (const [documentIndex, document] of documents.entries()) {
    const copy = await PDFDocument.load(templateBytes);
    const sourceForm = copy.getForm();
    sourceForm
      .getFields()
      .filter((field) => /^(IRASSignature|Signature1)$/.test(field.getName()))
      .forEach((field) => sourceForm.removeField(field));
    sourceForm.flatten();
    const sourcePages = copy.getPages();
    // GR-A has duplicate field names whose widget references can survive
    // flattening. Remove those source annotations before copying, then rebuild
    // clean editable fields on the combined output below.
    sourcePages.forEach((page) => page.node.delete(PDFName.of("Annots")));
    const indexes = type === "gra" ? [0, 1] : [0];
    const pages = await output.copyPages(copy, indexes);
    pages.forEach((page) => output.addPage(page));
    const font = await output.embedFont(StandardFonts.Helvetica);
    const prefix = `${type}_${documentIndex + 1}`;
    if (type === "gra") {
      addTextField(
        outputForm,
        pages[0],
        font,
        `${prefix}_transferor_name`,
        document.owner.name,
        [117, 806, 681, 827],
        10,
      );
      addTextField(
        outputForm,
        pages[0],
        font,
        `${prefix}_transferor_tax_reference`,
        document.owner.uen,
        [153, 778, 299, 797],
      );
      addTextField(
        outputForm,
        pages[0],
        font,
        `${prefix}_year_of_assessment`,
        document.yearOfAssessment,
        [550, 1016, 593, 1033],
      );
      addTextField(
        outputForm,
        pages[0],
        font,
        `${prefix}_total_transferred`,
        formatAmount(document.total),
        [217, 749, 321, 769],
      );
      // The supplied template contains this additional editable text area.
      addTextField(
        outputForm,
        pages[0],
        font,
        `${prefix}_template_part_1_detail`,
        "",
        [257, 833, 704, 845],
      );
      addTextField(
        outputForm,
        pages[1],
        font,
        `${prefix}_continuation_year_of_assessment`,
        document.yearOfAssessment,
        [504, 888, 548, 902],
      );
      addTextField(
        outputForm,
        pages[1],
        font,
        `${prefix}_continuation_tax_reference`,
        document.owner.uen,
        [113, 860, 256, 875],
      );
      GRA_ROWS.forEach((slot, index) => {
        const row = document.rows[index];
        const page = pages[slot.page];
        const rowPrefix = `${prefix}_transfer_${index + 1}`;
        addTextField(
          outputForm,
          page,
          font,
          `${rowPrefix}_priority`,
          row?.priority,
          slot.order,
        );
        addTextField(
          outputForm,
          page,
          font,
          `${rowPrefix}_claimant_name`,
          row?.counterparty.name,
          slot.name,
          8,
          true,
        );
        addTextField(
          outputForm,
          page,
          font,
          `${rowPrefix}_claimant_tax_reference`,
          row?.counterparty.uen,
          slot.taxRef,
          8,
        );
        Array.from({ length: 6 }, (_, i) => row?.amounts[i]).forEach(
          (amount, i) =>
            addTextField(
              outputForm,
              page,
              font,
              `${rowPrefix}_amount_${i + 1}`,
              row ? formatAmount(amount) : "",
              [slot.x[0], slot.ys[i], slot.x[1], slot.ys[i] + 15],
              8,
            ),
        );
        addTextField(
          outputForm,
          page,
          font,
          `${rowPrefix}_qualifying_from`,
          row ? printedDate(row.from) : "",
          slot.from,
          8,
        );
        addTextField(
          outputForm,
          page,
          font,
          `${rowPrefix}_qualifying_to`,
          row ? printedDate(row.to) : "",
          slot.to,
          8,
        );
      });
    } else {
      const page = pages[0];
      addTextField(
        outputForm,
        page,
        font,
        `${prefix}_year_of_assessment`,
        document.yearOfAssessment,
        [443, 782, 496, 799],
      );
      addTextField(
        outputForm,
        page,
        font,
        `${prefix}_claimant_name`,
        document.owner.name,
        [66, 605, 563, 625],
        10,
      );
      addTextField(
        outputForm,
        page,
        font,
        `${prefix}_claimant_tax_reference`,
        document.owner.uen,
        [99, 581, 233, 596],
      );
      GRB_Y.forEach((y, index) => {
        const row = document.rows[index];
        const rowPrefix = `${prefix}_transfer_${index + 1}`;
        addTextField(
          outputForm,
          page,
          font,
          `${rowPrefix}_priority`,
          row?.priority,
          [30, y, 57, y + 15],
          8,
        );
        addTextField(
          outputForm,
          page,
          font,
          `${rowPrefix}_transferor_name`,
          row?.counterparty.name,
          [64, y - 17, 246, y + 20],
          8,
          true,
        );
        addTextField(
          outputForm,
          page,
          font,
          `${rowPrefix}_transferor_tax_reference`,
          row?.counterparty.uen,
          [255, y, 356, y + 15],
          8,
        );
        addTextField(
          outputForm,
          page,
          font,
          `${rowPrefix}_qualifying_from`,
          row ? printedDate(row.from) : "",
          [393, y, 465, y + 15],
          8,
        );
        addTextField(
          outputForm,
          page,
          font,
          `${rowPrefix}_qualifying_to`,
          row ? printedDate(row.to) : "",
          [488, y, 563, y + 15],
          8,
        );
      });
    }
    addDeclarationFields(
      output,
      outputForm,
      pages,
      font,
      type,
      prefix,
      declarations[document.owner.id],
    );
  }
  return output.save();
}

export function downloadForm(bytes, type, year) {
  const url = URL.createObjectURL(
    new Blob([bytes], { type: "application/pdf" }),
  );
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `form-gr-${type === "gra" ? "a" : "b"}-YA${year}.pdf`;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
